import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EventNotFound from "@/components/EventNotFound/EventNotFound";
import Loader from "@/components/Loader/Loader";
import { useNotificationCenter } from "@/contexts/NotificationContext";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchAvailableEvents } from "@/store/slices/eventSlice";
import { fetchUserSubscription } from "@/store/slices/subscriptionSlice";
import { getCurrentUser, getMySubscription } from "@/lib/utils";
import routes from "@/routes/routes";
import type { Event } from "@/services/api/events";
import styles from "./Home.module.scss";
import Hey from "@/components/SVGComponents/Hey";
import Dining from "@/components/SVGComponents/Dining";
import {
  ArrowRightLineIcon,
  BellNavIcon,
} from "@/components/SVGComponents/MemberIcons";
import ConfirmAvailabilityModal, {
  type ConfirmAvailabilityEvent,
} from "@/pages/Home/components/ConfirmAvailabilityModal/ConfirmAvailabilityModal";

type HomeEventCard = Pick<
  Event,
  "description" | "eventDate" | "eventStartTime" | "eventEndTime" | "name" | "id" | "eventType"
> & { id: number | string };

type HomeLocationState = {
  openEventId?: number | string;
};

const Home = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    notificationCount,
    isNotificationOpen,
    openNotifications,
  } = useNotificationCenter();
  const { events, isLoading, error } = useAppSelector((state) => state.events);
  const [selectedEvent, setSelectedEvent] = useState<ConfirmAvailabilityEvent | null>(null);

  const userName =
      getCurrentUser("name") ||
      getCurrentUser("userName") ||
      getCurrentUser("email")?.split("@")?.[0]?.replace(/\./g, " ") ||
      "John";

  useEffect(() => {
    dispatch(
      fetchAvailableEvents({
        page: 0,
        size: 10,
      }),
    );
    dispatch(fetchUserSubscription());
  }, [dispatch]);

  const safeEvents: Event[] = events || [];
  const displayEvents = useMemo<HomeEventCard[]>(() => {
    if (error) {
      return [];
    }

    const seededEvents: HomeEventCard[] = safeEvents.map((event) => ({
      id: event.id,
      name: event.name,
      description: event.description || "A curated dinner experience",
      eventType: event.eventType,
      eventDate: event.eventDate,
      eventStartTime: event.eventStartTime,
      eventEndTime: event.eventEndTime,
    }));

    return seededEvents.slice(0, 4);
  }, [error, safeEvents]);


  const monthLabel = useMemo(() => {
    const firstDate = displayEvents[0]?.eventDate;
    const sourceDate = firstDate ? new Date(firstDate) : new Date();

    return sourceDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  }, [displayEvents]);

  const formatMonthYear = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });

  const formatDateDay = (dateString: string) =>
    String(new Date(dateString).getDate()).padStart(2, "0");

  const formatTime = (timeString: string) => {
    const time = new Date(timeString);

    if (isNaN(time.getTime())) {
      return "Invalid Time";
    }

    return time.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const getAvailabilityEvent = (event: HomeEventCard): ConfirmAvailabilityEvent => ({
    id: event.id,
    title: event.name,
  });

  useEffect(() => {
    const { openEventId } = (location.state as HomeLocationState) || {};

    if (!openEventId || getMySubscription()?.status !== "ACTIVE") {
      return;
    }

    const event = displayEvents.find(
      (item) => String(item.id) === String(openEventId),
    );

    if (event) {
      setSelectedEvent(getAvailabilityEvent(event));
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [displayEvents, location.pathname, location.state, navigate]);

  const refreshEvents = () => {
    dispatch(fetchAvailableEvents({ page: 0, size: 10 }));
  };

  const openQuestionnaire = (event: HomeEventCard) => {
    if (getMySubscription()?.status === "ACTIVE") {
      setSelectedEvent(getAvailabilityEvent(event));
      return;
    }

    navigate(routes.privateRoutes.DINE_WITH_FOUNDERS.path);
  };

  const closeAvailabilityModal = () => {
    setSelectedEvent(null);
  };

  const desktopSummary = isLoading
    ? "We're curating your upcoming tables right now."
    : error
      ? "We couldn't load your curated dinners right now."
      : `${displayEvents.length} upcoming tables curated for meaningful conversations.`;

  const mobileSummary = isLoading
    ? "We're curating your upcoming tables right now."
    : error
      ? "We couldn't load your curated dinners right now."
      : `${displayEvents.length} exclusive tables. Choose when you'd like to join.`;

  const emptyTitle = error ? "Unable to load events" : "No events available";

  return (
    <>
      <section className={styles.page}>
        <div className={styles.desktopContent}>
          <div className={styles.heroSection}>
            <div className={styles.heroCopy}>
              <h1 className={styles.title}>
                Welcome, <span>{userName}</span>
                <Hey />
              </h1>
              <h2 className={styles.subtitle}>
                {/* Your curated dinners for {monthLabel} */}
                Pick your dinner for this month.
              </h2>
              {/* <p className={styles.copy}>{desktopSummary}</p> */}
              Choose which dinner you'd like to join this month.
            </div>

            <div className={styles.heroIllustrationCard}>
              <div className={styles.heroGlow} />
              <Dining className={styles.heroIllustration} />
            </div>
          </div>

          <div className={styles.bodyGrid}>
            {isLoading ? (
              <div className={styles.fullSpanState}>
                <Loader fullscreen={false} size={96} />
              </div>
            ) : displayEvents.length > 0 ? (
              <>
                <div className={styles.featuredSection}>
                  {displayEvents.map((event) => (
                    <div key={event.id} className={styles.featuredCard}>
                      <div className={styles.featuredDateBlock}>
                        <span className={styles.featuredMonth}>
                          {formatMonthYear(event.eventDate)}
                        </span>
                        <strong>{formatDateDay(event.eventDate)}</strong>
                        <span className={styles.featuredTime}>
                          {formatTimeRange(
                            event.eventStartTime,
                            event.eventEndTime,
                          )}
                        </span>
                      </div>
                      <div className={styles.featuredContent}>
                        <h3>{event.name}</h3>

                        <p>{event.description}</p>

                        <div className={styles.featuredFooter}>
                          <span className={styles.featuredTag}>
                            {event.eventType || "Curated Dinner"}
                          </span>

                          <button
                            aria-label={`Open RSVP questions for ${event.name}`}
                            className={styles.featuredAction}
                            onClick={() => openQuestionnaire(event)}
                            type="button"
                          >
                            <ArrowRightLineIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className={styles.fullSpanState}>
                <EventNotFound title={emptyTitle} titleColor="primary" />
              </div>
            )}
          </div>
        </div>

        <div className={styles.mobileContent}>
          <div className={styles.mobileHeaderRow}>
            <h1 className={styles.mobileTitle}>
              Welcome, <span>{userName}</span>
            </h1>

            <button
              aria-label="Notifications"
              aria-expanded={isNotificationOpen}
              aria-haspopup="dialog"
              className={styles.mobileNotificationButton}
              onClick={openNotifications}
              type="button"
            >
              <BellNavIcon />
              {notificationCount > 0 ? (
                <span className={styles.mobileNotificationDot} />
              ) : null}
            </button>
          </div>

          <h2 className={styles.mobileSubtitle}>
            Pick your dinner for this month.
          </h2>
          <p className={styles.mobileCopy}>Choose which dinner you'd like to join this month.</p>

          <div className={styles.mobileCardsGrid}>
            {isLoading ? (
              <div className={styles.mobileStatePanel}>
                <Loader fullscreen={false} size={84} />
              </div>
            ) : displayEvents.length > 0 ? (
              displayEvents.map((event) => (
                <article
                  aria-label={`Open RSVP questions for ${event.name}`}
                  className={styles.mobileEventCard}
                  key={`mobile-${event.id}`}
                  onClick={() => openQuestionnaire(event)}
                  onKeyDown={(keyboardEvent) => {
                    if (
                      keyboardEvent.key === "Enter" ||
                      keyboardEvent.key === " "
                    ) {
                      keyboardEvent.preventDefault();
                      openQuestionnaire(event);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className={styles.mobileDateCard}>
                    <span>{formatMonthYear(event.eventDate)}</span>
                    <strong>{formatDateDay(event.eventDate)}</strong>
                    <small>
                      {formatTimeRange(
                        event.eventStartTime,
                        event.eventEndTime,
                      )}
                    </small>
                  </div>

                  <div className={styles.mobileEventCopy}>
                    <h3>{event.name}</h3>
                    <p>{event.description}</p>
                  </div>
                </article>
              ))
            ) : (
              <div className={styles.mobileStatePanel}>
                <EventNotFound title={emptyTitle} titleColor="primary" />
              </div>
            )}
          </div>
        </div>
      </section>

      <ConfirmAvailabilityModal
        event={selectedEvent}
        isOpen={Boolean(selectedEvent)}
        onClose={closeAvailabilityModal}
        onSuccess={refreshEvents}
      />
    </>
  );
};

export default Home;
